import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * @module useWeeklyReports
 * @description Custom hook for fetching weekly reports in the Mental Health Portal application.
 * Supports filtering, pagination, and automatically refetches when parameters change.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to fetch and manage weekly reports with filtering and pagination
 * @function useWeeklyReports
 * @param {Object} options - Configuration options for fetching reports
 * @param {boolean} [options.edited] - Filter by edited status
 * @param {boolean} [options.all] - Whether to fetch all reports regardless of filters
 * @param {number} [options.page=1] - Current page number for pagination
 * @param {number} [options.pageSize=20] - Number of reports per page
 * @returns {Object} Object containing data, loading state, and error state
 */
export function useWeeklyReports({
  edited,
  all,
  page = 1,
  pageSize = 20,
} = {}) {
  /**
   * @state {Object} data - The fetched reports data
   * @property {Array} reports - List of weekly reports
   * @property {Array} availableResources - List of resources that can be recommended
   * @property {number} total - Total count of reports matching the filter
   */
  const [data, setData] = useState({
    reports: [],
    availableResources: [],
    total: 0,
  });

  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(true);

  /**
   * @state {string|null} error - Error message if the API request fails, null otherwise
   */
  const [error, setError] = useState(null);

  /**
   * Effect hook to fetch reports when parameters change
   * @effect
   * @param {boolean} edited - Filter by edited status
   * @param {boolean} all - Whether to fetch all reports
   * @param {number} page - Current page number
   * @param {number} pageSize - Number of reports per page
   */
  useEffect(() => {
    /**
     * Fetches weekly reports from the API with the specified filters and pagination
     * @async
     * @function fetchReports
     * @returns {Promise<void>}
     */
    const fetchReports = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        if (typeof edited === 'boolean')
          params.append('edited', String(edited));
        if (typeof all === 'boolean') params.append('all', String(all));
        params.append('limit', pageSize);
        params.append('offset', (page - 1) * pageSize);

        const url = `list-weekly-reports?${params.toString()}`;
        const { data } = await api.get(url);
        setData(data);
      } catch (err) {
        console.error('Erro ao carregar os relatórios:', err);
        setError('Erro ao carregar os relatórios semanais');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [edited, all, page, pageSize]);

  /**
   * @typedef {Object} UseWeeklyReportsReturn
   * @property {Object} data - The fetched reports data
   * @property {Array} data.reports - List of weekly reports
   * @property {Array} data.availableResources - List of resources that can be recommended
   * @property {number} data.total - Total count of reports matching the filter
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string|null} error - Error message if the API request fails, null otherwise
   */

  /**
   * @returns {UseWeeklyReportsReturn} The hook's return values
   */
  return { data, loading, error };
}
